import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { AddToBag } from "@/components/AddToBag";
import { SHADES, SHADE_PRICE, collectionUrl, discountPct, formatINR, productUrl } from "@/data/catalog";
import { findShade, openConcierge } from "@/lib/concierge";
import { useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

const QUESTIONS = [
  { key: "eye_color", label: "Your eyes", options: ["Brown", "Dark brown / black", "Hazel", "Green", "Blue / grey"] },
  { key: "occasion", label: "The occasion", options: ["Everyday", "Office", "Date night", "Party", "Wedding / festive", "Bridal"] },
  { key: "finish", label: "The finish", options: ["Soft shimmer", "High shine", "Duo-chrome", "Bold glitter"] },
];
const LASH_HANDLES = { "Luxury Mink Lash": "luxury-mink-lash", "Faux Mink Lash Set": "faux-mink-lash-sets", "Natural Hair Eyelash": "natural-hair-lashes" };
const byId = (id) => SHADES.find((s) => s.id === id);
const swatch = (colors) => `radial-gradient(circle at 32% 28%, #fff8ec 0%, ${colors[0]} 22%, ${colors[1]} 75%)`;

const Result = ({ result, profile, onPick }) => {
  const shade = byId(result.shade);
  const live = useLiveProduct(shade);
  const price = live.live ? live : SHADE_PRICE;
  const off = discountPct(price.price, price.compare);
  return (
    <motion.div
      key={shade.id}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.6, ease: EASE }}
      data-testid="shade-finder-result"
      className="grid gap-8 sm:grid-cols-[auto_1fr] sm:items-center"
    >
      <div className="swatch-dot h-36 w-36 rounded-full sm:h-44 sm:w-44" style={{ background: swatch(shade.colors) }} />
      <div className="min-w-0">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-champagne">Your shade · {shade.finish}</p>
        <p data-testid="shade-finder-shade-name" className="mt-2 font-display text-4xl text-cream sm:text-5xl">{shade.name}</p>
        <p data-testid="shade-finder-why" className="mt-4 max-w-lg text-sm leading-relaxed text-cream/90 sm:text-base">{result.why}</p>
        {result.tip && <p className="mt-2 max-w-lg text-xs leading-relaxed text-stone sm:text-sm">{result.tip}</p>}
        <p className="mt-4 text-cream">
          <span data-testid="shade-finder-price">{formatINR(price.price)}</span>
          {off > 0 && <span className="ml-3 text-sm text-stone line-through">{formatINR(price.compare)}</span>}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <AddToBag p={live} testId="shade-finder-add-to-bag" className="btn-gold">Add {shade.name} to bag</AddToBag>
          <a href={productUrl(shade.handle)} data-testid="shade-finder-view-shade" className="btn-ghost">View shade</a>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone">Also try</span>
            {result.alternatives.map((id) => {
              const alt = byId(id);
              return (
                <button key={id} data-testid={`shade-finder-alt-${id}`} onClick={() => onPick(id)} aria-label={`Switch to ${alt.name}`} className="group flex items-center gap-2 text-cream/90 transition-colors hover:text-champagne">
                  <span className="swatch-dot h-7 w-7 rounded-full transition-transform duration-500 group-hover:scale-110" style={{ background: swatch(alt.colors) }} />
                  {alt.name}
                </button>
              );
            })}
          </div>
          <a href={collectionUrl(LASH_HANDLES[result.lash])} data-testid="shade-finder-lash-link" className="nav-link inline-flex items-center gap-1 text-cream">
            Pair with {result.lash} <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            data-testid="shade-finder-ask-concierge"
            onClick={() => openConcierge(`I picked Celestial ${shade.name} for ${profile.occasion.toLowerCase()} with ${profile.eye_color.toLowerCase()} eyes - how should I wear it and what lash goes with it?`)}
            className="inline-flex items-center gap-1.5 text-champagne transition-colors hover:text-cream"
          >
            <Sparkles className="h-3.5 w-3.5" /> Ask the concierge how to wear it
          </button>
        </div>
      </div>
    </motion.div>
  );
};

/** Three taps -> ChatGPT picks a Celestial shade (ids only; names, prices and links come from our catalog). */
export const ShadeFinder = () => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const ready = QUESTIONS.every((q) => profile[q.key]);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await findShade(profile));
    } catch {
      setError("The shade finder is taking a breather - please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const pick = (id) => setResult((r) => ({ ...r, shade: id, alternatives: [r.shade, ...r.alternatives.filter((a) => a !== id)].slice(0, 2) }));

  return (
    <div data-testid="shade-finder" className="relative mt-20 overflow-hidden rounded-[2rem] border border-champagne/20 bg-ink/60 p-6 backdrop-blur-xl sm:p-10 lg:mt-28 lg:p-14">
      <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(232,200,152,0.22),transparent_65%)] blur-2xl" />
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.28em] text-champagne"><Sparkles className="h-3.5 w-3.5" /> AI Shade Finder</p>
          <h3 className="mt-4 font-display text-4xl font-light leading-[0.98] text-cream sm:text-5xl">Three taps. <em className="italic text-champagne">One shade of light.</em></h3>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-stone sm:text-base">Answer three quick questions and our AI stylist picks the Celestial shade that will catch the light on you - with the lash to pair it with.</p>
          <div className="mt-8 space-y-6">
            {QUESTIONS.map((q) => (
              <div key={q.key} role="radiogroup" aria-label={q.label}>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone">{q.label}</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {q.options.map((o) => {
                    const on = profile[q.key] === o;
                    return (
                      <button
                        key={o}
                        role="radio"
                        aria-checked={on}
                        data-testid={`shade-finder-${q.key}-${o.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                        onClick={() => setProfile((p) => ({ ...p, [q.key]: o }))}
                        className={`rounded-full border px-4 py-2 text-xs tracking-wide transition-colors duration-300 ${on ? "border-champagne bg-champagne text-ink" : "border-cream/15 text-cream hover:border-champagne/60"}`}
                      >
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <button data-testid="shade-finder-submit" onClick={run} disabled={!ready || loading} className="btn-gold mt-8 disabled:cursor-not-allowed disabled:opacity-40">
            {loading ? "Reading the light…" : result ? "Find again" : "Find my shade"}
            <Sparkles className="h-4 w-4" />
          </button>
          {error && <p data-testid="shade-finder-error" className="mt-4 text-sm text-red-200">{error}</p>}
        </div>
        <div className="flex min-h-[260px] items-center lg:col-span-7">
          <AnimatePresence mode="wait">
            {result ? (
              <Result key={result.shade} result={result} profile={profile} onPick={pick} />
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-6" data-testid="shade-finder-empty">
                <div className="flex -space-x-5">
                  {SHADES.slice(0, 5).map((s, i) => (
                    <span key={s.id} className={`swatch-dot h-16 w-16 rounded-full border-2 border-ink sm:h-20 sm:w-20 ${loading ? "animate-pulse" : ""}`} style={{ background: swatch(s.colors), animationDelay: `${i * 120}ms` }} />
                  ))}
                </div>
                <p className="max-w-xs font-display text-2xl leading-snug text-cream/80">{loading ? "Matching your eyes to the Celestial range…" : "Eight Celestial shades. Your pick appears here."}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
